import React, { useState, useMemo } from "react";
import { useOrgDirectory } from "../../hooks/useOrgDirectory";
import { useSort } from "../../hooks/useSort";
import { fmtDate, fmtRelative } from "../../lib/dates";
import { HEALTH_LABEL } from "../../lib/ratings";
import { Button, Chip, Alert, EmptyState, SkeletonRows, SortHeader, Modal, Field } from "../../components";
import OrganizationFields, { EMPTY_ORGANIZATION, validateOrganization, cleanOrganization } from "./OrganizationFields";
import ScanResults from "./ScanResults";
import { useNavIntent } from "../../shell/intent";
import "./OrganizationsScreen.css";

// The landing surface, and the only client list.
//
// This screen used to have a twin under Business reviews showing the same rows with
// different columns. They were never two lists — the ids were the scanner's on both
// sides — so a client's scans and their review record are one table now, and nobody has
// to hold two rails in their head to see one customer.
//
// Rows expand in place rather than navigating. There is no organization detail endpoint,
// and inventing a route for data that does not exist would be a promise the API cannot
// keep. Editing the review profile is an explicit action in its own cell instead, so the
// row click keeps meaning exactly one thing.

const VIEWS = [
  { value: "", label: "All", countKey: "all" },
  { value: "client", label: "Client", countKey: "client" },
  { value: "prospect", label: "Prospect", countKey: "prospect" },
];

const HEALTH_FILTERS = [
  { value: "", label: "All health levels" },
  { value: "strong", label: "Strong" },
  { value: "watch", label: "Watch" },
  { value: "atrisk", label: "At risk" },
  { value: "unscored", label: "Unscored" },
];

// Health badges use the health palette, not the success/warning/danger state colours.
// "watch" used to map to "neutral" — a tone with no rule behind it — so it rendered grey
// and looked identical to an unscored organization.
const HEALTH_TONE = {
  strong: "health-strong",
  watch: "health-watch",
  atrisk: "health-atrisk",
  unscored: "health-unscored",
};

// Whole currency units, abbreviated once past a thousand: the KPI is a headline, and
// "$47,280" in a tile competes with the number of clients next to it.
const money = (n) => {
  if (n === null || n === undefined) return "–";
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toLocaleString()}`;
};

// The review columns are dropped entirely when the Business Review Service is
// unreachable, rather than rendered as a row of em-dashes that reads like missing data
// instead of an absent service.
const BASE_COLS = [
  { key: "name",         label: "Name" },
  { key: "created_date", label: "Created" },
  { key: "update_date",  label: "Last updated" },
];

const REVIEW_COLS = [
  { key: "relationship", label: "Relationship" },
  { key: "score",        label: "Technology health" },
  { key: "mrr",          label: "MRR", num: true },
  { key: "next_review",  label: "Next review" },
  { key: "owner_name",   label: "Owner" },
];

const OrganizationsScreen = () => {
  const {
    organizations, loading, refreshing, error, reload, create,
    reviewsAvailable, reviewError, kpis, counts, filters, setFilter, saveProfile,
  } = useOrgDirectory();

  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  // From the rail flyout: "New account", or one account picked from its list — opened and
  // scrolled to, with any search cleared so a filter cannot be hiding it. Waits for the
  // list so the row exists to scroll to.
  useNavIntent((intent) => {
    if (intent.type === "new") setShowCreate(true);
    if (intent.type === "open" && intent.id != null) {
      setSearch("");
      setOpenId(intent.id);
      requestAnimationFrame(() =>
        document.querySelector(`[data-org-id="${intent.id}"]`)?.scrollIntoView({ block: "start", behavior: "smooth" })
      );
    }
  }, !loading);

  // Search stays on the client. The review endpoint has a search param, but it matches
  // the name only and the hook behind it refetches on every keystroke with no debounce —
  // so this both matches more (description too) and costs nothing.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter(
      (o) => (o.name || "").toLowerCase().includes(q) || (o.description || "").toLowerCase().includes(q)
    );
  }, [organizations, search]);

  const cols = useMemo(
    () => (reviewsAvailable ? [BASE_COLS[0], ...REVIEW_COLS, BASE_COLS[1], BASE_COLS[2]] : BASE_COLS),
    [reviewsAvailable]
  );

  const { sort, toggleSort, sorted } = useSort(filtered, "name", ["created_date", "update_date", "next_review"]);

  // Every column, plus the edit cell and the chevron.
  const span = cols.length + 2;

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Organizations</h2>
          <p>Every client whose scan data lands in this workspace, with what a review adds to it.</p>
        </div>
        <div className="v2-pagehead-actions">
          <Chip><b>{sorted.length}</b> {sorted.length === 1 ? "organization" : "organizations"}</Chip>
          <Button onClick={reload} disabled={refreshing}>{refreshing ? "Refreshing…" : "Refresh"}</Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>New organization</Button>
        </div>
      </div>

      {reviewsAvailable && kpis && (
        <div className="v2-metrics">
          <div className="v2-metric">
            <div className="v2-metric-label">Active clients</div>
            <div className="v2-metric-value">{kpis.active_clients}</div>
          </div>
          <div className="v2-metric">
            <div className="v2-metric-label">Open prospects</div>
            <div className="v2-metric-value">{kpis.open_prospects}</div>
            <div className="v2-metric-sub">{kpis.prospects_with_assessments} with active assessments</div>
          </div>
          <div className="v2-metric">
            <div className="v2-metric-label">Managed revenue</div>
            <div className="v2-metric-value">{money(kpis.managed_revenue)}</div>
          </div>
          <div className="v2-metric">
            <div className="v2-metric-label">Needs attention</div>
            <div className="v2-metric-value" style={kpis.needs_attention ? { color: "var(--v2-danger)" } : undefined}>
              {kpis.needs_attention}
            </div>
            <div className="v2-metric-sub">at risk, or a review past its date</div>
          </div>
        </div>
      )}

      {reviewsAvailable && (
        <div className="v2-views" style={{ marginTop: ".875rem" }}>
          {VIEWS.map((v) => (
            <button key={v.value || "all"} type="button"
                    className={`v2-view${filters.relationship === v.value ? " on" : ""}`}
                    aria-current={filters.relationship === v.value ? "page" : undefined}
                    onClick={() => setFilter({ relationship: v.value })}>
              {v.label}
              {counts[v.countKey] != null && <span className="n">{counts[v.countKey]}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="v2-orgsearch">
        <input
          className="v2-input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search organizations"
          aria-label="Search organizations"
        />
        {reviewsAvailable && (
          <>
            <select className="v2-input v2-cadence" value={filters.health}
                    aria-label="Filter by technology health"
                    onChange={(e) => setFilter({ health: e.target.value })}>
              {HEALTH_FILTERS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
            </select>
            <select className="v2-input v2-cadence" value={filters.status}
                    aria-label="Filter by status"
                    onChange={(e) => setFilter({ status: e.target.value })}>
              <option value="">Any status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </>
        )}
      </div>

      <Alert>{error}</Alert>

      {/* Not an Alert: the scanner list loaded fine and the page is usable. This says
          which columns are missing and why, without dressing it up as a page failure. */}
      {reviewError && (
        <p className="v2-hint">
          Review details are unavailable — {reviewError} Organizations are listed from the
          scanner; relationship, health, revenue and schedule columns are hidden until the
          Business Review Service responds.
        </p>
      )}

      <div className="v2-tablewrap">
        <table className="v2-table">
          <thead>
            <tr>
              {cols.map((c) => (
                <SortHeader key={c.key} label={c.label} colKey={c.key} sort={sort} onSort={toggleSort}
                            className={c.num ? "v2-num" : undefined} />
              ))}
              {reviewsAvailable && <th style={{ width: 60 }} />}
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={5} cols={span} widths={["45%", "70%", "55%", "40%", "20%"]} />
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={span}>
                  <EmptyState title={search ? "No matches" : "No organizations yet"}>
                    {search ? `Nothing matches "${search}".` : "Create one to start attaching scan data."}
                  </EmptyState>
                </td>
              </tr>
            ) : (
              sorted.map((org) => {
                const open = openId === org.id;
                return (
                  <React.Fragment key={org.id}>
                    <tr
                      data-org-id={org.id}
                      className={`v2-orgrow${open ? " open" : ""}`}
                      onClick={() => setOpenId(open ? null : org.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(open ? null : org.id); }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={open}
                    >
                      <td className="v2-orgname">
                        {org.name}
                        {reviewsAvailable && (
                          <div className="v2-orgsub">
                            {[org.industry, org.employees ? `${org.employees} employees` : null]
                              .filter(Boolean).join(" • ") || "No detail recorded"}
                          </div>
                        )}
                      </td>

                      {reviewsAvailable && (
                        <>
                          <td>
                            <span className={`v2-rbadge${org.relationship === "client" ? " v2-rbadge--accent" : ""}`}>
                              {org.relationship === "client" ? "Client" : "Prospect"}
                            </span>
                          </td>
                          <td>
                            {/* An organization the review service did not return has no
                                health at all, which is not the same as "unscored" —
                                fall back to the neutral tone rather than emitting a
                                v2-rbadge--undefined class. */}
                            <span className={`v2-rbadge v2-rbadge--${HEALTH_TONE[org.health] || "health-unscored"}`}>
                              {org.score === null || org.score === undefined
                                ? "Unscored"
                                : `${org.score} ${HEALTH_LABEL[org.health]}`}
                            </span>
                            <span className="v2-dim" style={{ marginLeft: ".4375rem", fontSize: ".75rem" }}>
                              {org.assessment_count > 0 ? "Latest assessment" : "No assessment yet"}
                            </span>
                          </td>
                          <td className="v2-num">
                            {org.mrr === null || org.mrr === undefined ? "–" : `$${org.mrr.toLocaleString()}`}
                          </td>
                          <td>
                            {!org.next_review ? <span className="v2-dim">Not scheduled</span>
                              : org.overdue ? <span style={{ color: "var(--v2-danger)", fontWeight: 600 }}>Overdue</span>
                              : fmtDate(org.next_review)}
                          </td>
                          <td className="v2-dim">
                            {org.owner_name ? org.owner_name.slice(0, 2).toUpperCase() : "—"}
                          </td>
                        </>
                      )}

                      <td className="v2-dim">{fmtDate(org.created_date)}</td>
                      <td className="v2-dim">{fmtRelative(org.update_date)}</td>

                      {reviewsAvailable && (
                        <td>
                          {/* stopPropagation, or editing a profile would also toggle the
                              scan panel underneath the modal. */}
                          <button type="button" className="v2-linkcell"
                                  aria-label={`Edit review profile for ${org.name}`}
                                  onClick={(e) => { e.stopPropagation(); setEditing(org); }}>
                            Edit
                          </button>
                        </td>
                      )}
                      <td className="v2-dim v2-orgchev" aria-hidden="true">{open ? "⌄" : "›"}</td>
                    </tr>
                    {open && (
                      <tr className="v2-orgpanelrow">
                        <td colSpan={span}>
                          {/* Pinned to the visible left edge: the panel inherits the
                              table's scroll width, so without this the controls sit
                              off-screen whenever the table overflows. */}
                          <div className="v2-orgpanel">
                            <ScanResults organizationId={org.id} organizationName={org.name} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onCreate={create} />}
      {editing && (
        <ProfileModal
          organization={editing}
          onClose={() => setEditing(null)}
          onSave={saveProfile}
        />
      )}
    </div>
  );
};

// Create-organization is duplicated three times in v1 (DomainScanner, GenerateScanLink,
// AdminPanel). One copy here; its fields live in OrganizationFields so New assessment's
// inline form asks for exactly the same things.
const CreateOrgModal = ({ onClose, onCreate }) => {
  const [form, setForm] = useState(EMPTY_ORGANIZATION);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    const invalid = validateOrganization(form);
    if (invalid) { setErr(invalid); return; }
    setBusy(true); setErr("");
    try { await onCreate(cleanOrganization(form)); onClose(); }
    catch (e) { setErr(e.message || "Could not create the organization."); }
    finally { setBusy(false); }
  };

  return (
    <Modal
      title="New organization"
      onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create"}</Button>
      </>}
    >
      <Alert>{err}</Alert>
      <OrganizationFields value={form} onChange={setForm} onSubmit={submit} />
    </Modal>
  );
};

// The review record that hangs off a scanner organization. Name, website and contacts
// are NOT here: the scanner has no update endpoint for them, and offering boxes that
// cannot save would be worse than not offering them.
const ProfileModal = ({ organization, onClose, onSave }) => {
  const [form, setForm] = useState({
    relationship: organization.relationship || "prospect",
    industry: organization.industry || "",
    employees: organization.employees ?? "",
    mrr: organization.mrr ?? "",
    status: organization.status || "active",
  });
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setBusy(true);
    const ok = await onSave(organization.id, {
      relationship: form.relationship,
      industry: form.industry || null,
      // An empty box means "not recorded", not zero.
      employees: form.employees === "" ? null : Number(form.employees),
      mrr: form.mrr === "" ? null : Number(form.mrr),
      status: form.status,
    });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Modal title={organization.name} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
      </>}>
      <Field label="Relationship">
        <select className="v2-input" value={form.relationship}
                onChange={(e) => set({ relationship: e.target.value })}>
          <option value="prospect">Prospect</option>
          <option value="client">Client</option>
        </select>
      </Field>
      <div className="v2-grid2">
        <Field label="Industry">
          <input className="v2-input" value={form.industry} placeholder="Healthcare"
                 onChange={(e) => set({ industry: e.target.value })} />
        </Field>
        <Field label="Employees">
          <input className="v2-input" type="number" min="0" value={form.employees}
                 onChange={(e) => set({ employees: e.target.value })} />
        </Field>
      </div>
      <div className="v2-grid2">
        <Field label="Monthly recurring revenue" hint="Whole currency units. Blank means not recorded.">
          <input className="v2-input" type="number" min="0" value={form.mrr}
                 onChange={(e) => set({ mrr: e.target.value })} />
        </Field>
        <Field label="Status">
          <select className="v2-input" value={form.status} onChange={(e) => set({ status: e.target.value })}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
};

export default OrganizationsScreen;
